import { MutableRefObject, Ref, useCallback, useRef } from 'react';

/**
 * Composes multiple refs (RefObjects or callback refs) into a single
 * callback ref. The returned function has stable identity across renders,
 * which is important under React's strict callback-ref semantics: an
 * identity-unstable composed ref would be detached (called with `null`)
 * and re-attached on every render, looping if any inner ref dispatched
 * state on call.
 *
 * The latest input refs are forwarded at every attach / detach (including
 * on unmount, when React calls the returned ref with `null`). If a parent
 * swaps its ref between mount and unmount, the new ref will receive the
 * cleanup `null` call but won't observe the node mid-life — same trade-off
 * as `@radix-ui/react-compose-refs`.
 *
 * Usage:
 *   const innerRef = useRef<HTMLInputElement>(null);
 *   const setRef = useCombinedRefs(forwardedRef, innerRef);
 *   return <input ref={setRef} ... />;
 *   // read DOM via innerRef.current
 */
export const useCombinedRefs = <T>(
  ...refs: (Ref<T> | undefined | null)[]
): ((node: T | null) => void) => {
  // Mirror the spread args into a ref so the stable callback below reads the
  // latest inputs without subscribing to them. Render-phase write is the
  // documented React pattern for "ref-as-latest-value" and is idempotent
  // under StrictMode double-invoke.
  const refsRef = useRef(refs);
  refsRef.current = refs;

  return useCallback((node: T | null) => {
    refsRef.current.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(node);
      } else {
        (ref as MutableRefObject<T | null>).current = node;
      }
    });
  }, []);
};
